import { ref, onUnmounted } from 'vue'

/**
 * @description 判断点 (x, y) 是否在矩形 rect 向外扩展 padding 的区域内
 * @param {number} x - 鼠标 clientX
 * @param {number} y - 鼠标 clientY
 * @param {DOMRect} rect - 元素 getBoundingClientRect()
 * @param {number} padding - 扩展像素
 * @returns {boolean}
 */
function isInExpandedRect(x, y, rect, padding) {
  return (
    x >= rect.left - padding &&
    x <= rect.right + padding &&
    y >= rect.top - padding &&
    y <= rect.bottom + padding
  )
}

/**
 * @description 卡片 hover 按距离还原：激活后边缘不频繁切换，鼠标移开卡片一定距离后再还原
 * @param {number} [leaveDistance=48] - 鼠标与卡片边界距离超过此值（px）才清除 hover
 * @returns {{ isHovered: import('vue').Ref<boolean>, onEnter: () => void, onLeave: (ev?: MouseEvent, cardEl?: HTMLElement) => void }}
 */
export function useHoverDelay(leaveDistance = 48) {
  const isHovered = ref(false)
  let moveHandler = null
  let fallbackTimer = null
  const FALLBACK_MS = 2000

  const clearLeaveCheck = () => {
    if (moveHandler) {
      document.removeEventListener('mousemove', moveHandler)
      moveHandler = null
    }
    if (fallbackTimer) {
      clearTimeout(fallbackTimer)
      fallbackTimer = null
    }
  }

  const onEnter = () => {
    clearLeaveCheck()
    isHovered.value = true
  }

  const onLeave = (ev, cardEl) => {
    if (!import.meta.client) {
      isHovered.value = false
      return
    }
    const el = cardEl || (ev && ev.currentTarget)
    if (!el) {
      isHovered.value = false
      return
    }
    const rect = el.getBoundingClientRect()
    const px = ev && typeof ev.clientX === 'number' ? ev.clientX : rect.left
    const py = ev && typeof ev.clientY === 'number' ? ev.clientY : rect.top
    if (!isInExpandedRect(px, py, rect, leaveDistance)) {
      isHovered.value = false
      return
    }
    moveHandler = (e) => {
      const r = el.getBoundingClientRect()
      if (!isInExpandedRect(e.clientX, e.clientY, r, leaveDistance)) {
        isHovered.value = false
        clearLeaveCheck()
      }
    }
    document.addEventListener('mousemove', moveHandler)
    fallbackTimer = setTimeout(() => {
      isHovered.value = false
      clearLeaveCheck()
    }, FALLBACK_MS)
  }

  onUnmounted(clearLeaveCheck)

  return { isHovered, onEnter, onLeave }
}

/**
 * @description 列表卡片 hover 按距离还原（按索引），激活后边缘不频繁切换，鼠标移开卡片一定距离后再还原
 * @param {number} [leaveDistance=48] - 鼠标与卡片边界距离超过此值（px）才清除 hover
 * @returns {{ hoveredIndex: import('vue').Ref<number|null>, onEnter: (index: number) => void, onLeave: (ev?: MouseEvent, cardEl?: HTMLElement) => void }}
 */
export function useHoverDelayIndex(leaveDistance = 48) {
  const hoveredIndex = ref(null)
  let moveHandler = null
  let fallbackTimer = null
  const FALLBACK_MS = 2000

  const clearLeaveCheck = () => {
    if (moveHandler) {
      document.removeEventListener('mousemove', moveHandler)
      moveHandler = null
    }
    if (fallbackTimer) {
      clearTimeout(fallbackTimer)
      fallbackTimer = null
    }
  }

  const onEnter = (index) => {
    clearLeaveCheck()
    hoveredIndex.value = index
  }

  const onLeave = (ev, cardEl) => {
    if (!import.meta.client) {
      hoveredIndex.value = null
      return
    }
    const el = cardEl || (ev && ev.currentTarget)
    if (!el) {
      hoveredIndex.value = null
      return
    }
    const rect = el.getBoundingClientRect()
    const px = ev && typeof ev.clientX === 'number' ? ev.clientX : rect.left
    const py = ev && typeof ev.clientY === 'number' ? ev.clientY : rect.top
    if (!isInExpandedRect(px, py, rect, leaveDistance)) {
      hoveredIndex.value = null
      return
    }
    moveHandler = (e) => {
      const r = el.getBoundingClientRect()
      if (!isInExpandedRect(e.clientX, e.clientY, r, leaveDistance)) {
        hoveredIndex.value = null
        clearLeaveCheck()
      }
    }
    document.addEventListener('mousemove', moveHandler)
    fallbackTimer = setTimeout(() => {
      hoveredIndex.value = null
      clearLeaveCheck()
    }, FALLBACK_MS)
  }

  onUnmounted(clearLeaveCheck)

  return { hoveredIndex, onEnter, onLeave }
}
